<template>
  <div class="timerSetter">
    <div
      aria-hidden="true"
      class="target"
      @mousedown="onMouseDown"
      @mouseup="onMouseUp"
      @mousemove="onMouseMove"
      @mouseOut="onMouseOut"
      :class="[iAmSetting ? 'grabbing' : '']"
    ></div>
    <div class="inputs visuallyHidden">
      <label for="setTime">Time in minutes </label>
      <input
        id="setTime"
        type="number"
        v-model="inputMins"
        :max="maxMins"
        @keydown.up.prevent="inputMore"
        @keydown.down.prevent="inputLess"
        @keyup.enter.prevent="go"
        ref="textInput"
      />
    </div>
  </div>
</template>
<script setup>
import { ref, computed, getCurrentInstance, onMounted, nextTick } from 'vue';
import { watchDebounced } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { useFrameStore } from '@/store/pinia/frame';
import { useTimeStore } from '@/store/pinia/time';
import { useTimeDisplay } from '@/composables/useTimeDisplay';
import { usePhaseStore } from '@/store/pinia/phase';

import { asUnit } from '@/resources/time-helpers';

const { $meetingmanager } = getCurrentInstance().appContext.config.globalProperties;

const props = defineProps({
  type: {
    type: String,
    default: 'phase',
  },
});

const frameStore = useFrameStore();
const timeStore = useTimeStore();
const phaseStore = usePhaseStore();
const { mouseDown } = storeToRefs(frameStore);
const { setupPhase, settingUp } = storeToRefs(phaseStore);
const { iAmSetting } = storeToRefs(timeStore);
const { timeData, settingTime } = useTimeDisplay({ type: props.type });

const rawProp = ref(0);
const tempProp = ref(0);
const snappedProp = ref(0);
const prevProp = ref(0);
const inputMins = ref(1);
const latestPublishedTime = ref(0);

const textInput = ref(null);

onMounted(() => {
  let initValue = settingTime.value ? settingTime.value / 60 : timeData.value.defaultTime / 60;
  inputMins.value = initValue;
  textInput.value.focus();
});

const maxSettingTime = computed(() => {
  return timeData.value.max * 1000;
});

const maxMins = computed(() => {
  return asUnit(maxSettingTime.value, 'minutes');
});

const stepArray = computed(() => {
  let steps = [0.5, 1, 2, 3, 4];
  let step = 5;
  let max = timeData.value.max / 60;
  while (step <= max) {
    steps.push(step);
    step += 5;
  }
  return steps;
});

function onMouseDown() {
  timeStore.updateIAmSetting(true);
}
function onMouseUp(e) {
  timeStore.updateIAmSetting(false);
  trackMouse(e, true);
}

const onMouseMove = computed(() => {
  return mouseDown.value ? trackMouse : null;
});

const stepIndex = ref(0);

watchDebounced(inputMins, updateFromInput, {
  debounce: 500,
});

function updateFromInput(v) {
  if (!settingUp.value) {
    return false;
  }
  let seconds = parseInt(v) * 60;
  let snapped = snapSeconds(seconds);
  inputMins.value = snapped / 60;
  stepIndex.value = stepArray.value.indexOf(snapped / 60);
  let prop = (snapped / maxSettingTime.value) * 1000;
  finishTracking(prop);
  textInput.value.select();
}

function inputMore() {
  let index = stepIndex.value + 1;
  if (index < stepArray.value.length) {
    inputMins.value = stepArray.value[index];
    stepIndex.value = index;
    updateFromInput(stepArray.value[index]);
  }
}

function inputLess() {
  let index = stepIndex.value - 1;
  if (index >= 0) {
    inputMins.value = stepArray.value[index];
    stepIndex.value = index;
    updateFromInput(stepArray.value[index]);
  }
}

function trackMouse(e, snap) {
  const deltaX = e.offsetX - e.target.clientWidth / 2;
  const deltaY = e.offsetY - e.target.clientHeight / 2;
  const radians = Math.atan2(deltaY, deltaX);
  let prop = radians / (2 * Math.PI);
  prop = prop < 0 ? prop + 1 : prop;
  prop = (prop + 0.25) % 1; // 0.25 is 12 o'clock

  // Handle the 0 threshold
  if (prop > 0.95 && prevProp.value < 0.05) {
    prop = prevProp.value;
  } else if (prevProp.value > 0.95 && prop < 0.05) {
    // seconds = setupTime.max;
    prop = prevProp.value;
    prop = prevProp.value;
  }

  rawProp.value = prop;
  timeStore.updateQuickSettingProp(prop);

  if (snap) {
    finishTracking(prop);
  }
}

function finishTracking(prop) {
  let finalProp = snapProp(prop);
  rawProp.value = finalProp;
  snappedProp.value = finalProp;
  tempProp.value = finalProp;
  prevProp.value = finalProp;
  //timeStore.updateQuickSettingProp(finalProp);

  if (props.type === 'meetingSetter') {
    // do the meeting version
  } else {
    // calculate the time
    const timeMs = Math.floor(finalProp * maxSettingTime.value);
    const timeSecs = Math.round(timeMs / 1000);
    if (timeSecs === latestPublishedTime.value) {
      return; // no change, so don't publish
    }

    // Update our local
    var args = {
      total: Math.round(asUnit(timeMs, 'minutes')) * 60,
      screen: setupPhase.value,
      max: timeData.value.max,
      what: 'publish',
    };
    timeStore.updateSettingTime(args);

    // publish the time
    $meetingmanager.changeNextTime(timeSecs); // send time in seconds
    latestPublishedTime.value = timeSecs;

    nextTick(() => {
      inputMins.value = Math.round(timeMs / 60000);
      textInput.value.focus();
      textInput.value.select();
    });
  }
}

function getStep(s) {
  if (props.type === 'meetingSetter') {
    if (s >= 5400) {
      return 1800;
    } else {
      return 900;
    }
  } else {
    if (s < 50) {
      return 30;
    } else if (s >= 50 && s <= 300) {
      return 60;
    } else {
      return 300;
    }
  }
}

function snapSeconds(seconds) {
  let minimum = props.type === 'meetingSetter' ? 900 : 30;
  let max = timeData.value.max;
  let step = getStep(seconds);

  seconds = seconds > minimum ? seconds : minimum;
  seconds = seconds < max ? seconds : max;

  let s = Math.round(seconds / step) * step;
  return s;
}

function snapProp(prop) {
  let seconds = Math.floor((maxSettingTime.value / 1000) * prop);
  let s = snapSeconds(seconds);
  return (s / maxSettingTime.value) * 1000;
}

function onMouseOut() {
  timeStore.updateIAmSetting(false);
}

function go() {
  const startButton = document.getElementById('sidebarButton-start');
  if (startButton) {
    startButton.click();
  }
}
</script>
<style scoped lang="scss">
.timerSetter {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  border-radius: 50%;
  .target {
    cursor: grab;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    &.grabbing {
      cursor: grabbing;
    }
  }
}
</style>
